/**
 * IQChallenge — public challenge view + multi-step run flow.
 *
 * Route: /iq-challenge/:code
 * The page is intentionally PUBLIC for read so OG scrapers and unauth'd
 * users see a rich preview ("Coach X invites you to a 5-scenario IQ
 * challenge"). Taking the challenge requires login + premium.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PlayAnimation from '@/components/PlayAnimation';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import { api, API } from '@/lib/api';
import {
  Trophy, ChevronRight, Loader2, Lock, CheckCircle2, XCircle, ArrowLeft,
  Brain, Target, Sparkles, Share2, Crown, Medal, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import ShareInviteButton from '@/components/ShareInviteButton';
import { buildIQScenarioSteps } from '@/lib/iqScenarioAnimation';

const LANG_TO_BCP47 = { es: 'es-ES', en: 'en-US', fr: 'fr-FR' };
const CATEGORY_BADGE = {
  offensive: { bg: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300' },
  defensive: { bg: 'bg-red-500/15 border-red-400/40 text-red-300' },
  complete:  { bg: 'bg-gold/15 border-gold/40 text-gold' },
};

export default function IQChallenge() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user } = useAuth();

  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState('preview'); // preview | running | done
  const [attemptId, setAttemptId] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [scenarioDetail, setScenarioDetail] = useState(null);
  const [reactionStart, setReactionStart] = useState(null);
  const [results, setResults] = useState([]); // [{scenario, result}]
  const [submitting, setSubmitting] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);

  // Load the public challenge metadata on mount. Works without auth.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/iq/challenges/${code}?lang=${encodeURIComponent(lang || 'es')}`)
      .then(r => { if (alive) setChallenge(r.data); })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || 'Challenge not found'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, lang]);

  // Leaderboard fetch is independent from auth and re-fetched after each
  // completed challenge run so the user immediately sees their new rank.
  const fetchLeaderboard = useCallback(() => {
    api.get(`/iq/challenges/${code}/leaderboard`)
      .then(r => setLeaderboard(r.data))
      .catch(() => {});
  }, [code]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const canStart = !!user && challenge?.viewer_is_premium;
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/iq-challenge/${code}` : '';

  // ---------------- start / fetch next scenario ----------------
  const loadNextScenario = useCallback(async (idx) => {
    if (!challenge) return;
    const sid = challenge.scenarios[idx]?.id;
    if (!sid) return;
    try {
      const { data } = await api.get(`/iq/scenarios/${sid}?lang=${encodeURIComponent(lang || 'es')}`);
      setScenarioDetail(data);
      setReactionStart(Date.now());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error loading scenario');
    }
  }, [challenge, lang]);

  const startChallenge = async () => {
    if (!user) {
      navigate('/login?next=' + encodeURIComponent(`/iq-challenge/${code}`));
      return;
    }
    if (!challenge?.viewer_is_premium) {
      toast.error(t('iq_premium_required'));
      return;
    }
    try {
      const { data } = await api.post(`/iq/challenges/${code}/start`);
      setAttemptId(data.attempt_id);
      setResults([]);
      setCurrentIdx(0);
      setPhase('running');
      await loadNextScenario(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start');
    }
  };

  const decide = async (optionId) => {
    if (!scenarioDetail || submitting) return;
    setSubmitting(true);
    const reaction = reactionStart ? (Date.now() - reactionStart) / 1000 : 0;
    try {
      const { data } = await api.post(`/iq/challenges/${code}/decide?lang=${encodeURIComponent(lang || 'es')}`, {
        scenario_id: scenarioDetail.id,
        option_id: optionId,
        reaction_seconds: reaction,
        attempt_id: attemptId,
      });
      const stepResult = { scenario: scenarioDetail, result: data, chosenOption: optionId };
      const nextResults = [...results, stepResult];
      setResults(nextResults);

      const next = currentIdx + 1;
      if (next >= challenge.scenarios.length) {
        setPhase('done');
        setScenarioDetail(null);
        fetchLeaderboard();
      } else {
        setCurrentIdx(next);
        setScenarioDetail(null);
        // Short pause so the player sees the score animate before the
        // next scenario loads.
        setTimeout(() => loadNextScenario(next), 100);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const totalScore = useMemo(
    () => results.reduce((s, r) => s + (r.result?.score_awarded || 0), 0),
    [results]
  );
  const correctCount = useMemo(
    () => results.filter(r => r.result?.is_optimal).length,
    [results]
  );

  // ---------------- RENDER ----------------
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !challenge) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto text-center py-20" data-testid="iq-challenge-not-found">
          <div className="text-6xl mb-3">🎯</div>
          <h1 className="font-h uppercase text-3xl mb-2">{t('iqc_not_found_h1')}</h1>
          <p className="text-gray-400 mb-6">{t('iqc_not_found_body')}</p>
          <Link to="/iq-simulator" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider hover:bg-gold-light">
            <ArrowLeft className="w-4 h-4" /> {t('iq_back_to_hub')}
          </Link>
        </div>
      </Layout>
    );
  }

  // ============== Phase: PREVIEW ==============
  if (phase === 'preview') {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto" data-testid="iq-challenge-preview">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">
            {t('iqc_eyebrow')}
          </div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl mb-2" data-testid="iq-challenge-title">
            {challenge.title}
          </h1>
          <p className="text-gray-400 mb-6 text-sm">
            {t('iqc_created_by')} <span className="text-gold">{challenge.creator_name}</span>
            {' · '}
            <span className="font-mono text-gold/70">{code}</span>
            {' · '}
            <span>{challenge.attempts_count} {t('iqc_attempts')}</span>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="iq-challenge-scenarios">
            {challenge.scenarios.map((s, i) => (
              <div key={s.id} className="bg-card-steel border border-gold/30 rounded-lg p-4 flex items-start gap-3">
                <div className="font-h text-2xl text-gold w-8 flex-shrink-0">#{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${CATEGORY_BADGE[s.category]?.bg || ''}`}>
                      {s.category === 'offensive' ? t('iq_cat_offensive')
                        : s.category === 'defensive' ? t('iq_cat_defensive')
                        : t('iq_cat_complete')}
                    </span>
                    <span className="text-[10px] font-mono uppercase text-gray-500">{s.difficulty}</span>
                  </div>
                  <div className="font-h uppercase text-sm text-white">{s.title}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border-2 border-gold/40 bg-gradient-to-br from-gold/10 to-transparent p-5 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-6 h-6 text-gold" />
              <div className="font-h uppercase text-lg text-white">{t('iqc_how_h')}</div>
            </div>
            <ul className="text-sm text-gray-300 space-y-1 ms-9 list-disc">
              <li>{t('iqc_how_1')}</li>
              <li>{t('iqc_how_2')}</li>
              <li>{t('iqc_how_3')}</li>
            </ul>
          </div>

          <div className="flex flex-wrap items-center gap-3" data-testid="iq-challenge-cta-row">
            {canStart ? (
              <button
                onClick={startChallenge}
                data-testid="iqc-start-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light"
              >
                {t('iqc_take_challenge')} <ChevronRight className="w-5 h-5" />
              </button>
            ) : !user ? (
              <Link
                to={`/login?next=${encodeURIComponent('/iq-challenge/' + code)}`}
                data-testid="iqc-login-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light"
              >
                {t('iqc_login_to_take')} <ChevronRight className="w-5 h-5" />
              </Link>
            ) : (
              <Link
                to="/pricing"
                data-testid="iqc-upgrade-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light"
              >
                <Lock className="w-4 h-4" /> {t('iqc_upgrade_to_take')}
              </Link>
            )}
            <ShareInviteButton
              url={shareUrl}
              title={challenge.title}
              text={`${challenge.creator_name} · ${challenge.title} — PlayMaster IA Challenge: ${shareUrl}`}
              variant="default"
              data-testid="iqc-share-btn"
            />
            <Link
              to="/iq-simulator"
              className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> {t('iq_back_to_hub')}
            </Link>
          </div>

          {leaderboard && leaderboard.top && leaderboard.top.length > 0 && (
            <div className="mt-8" data-testid="iqc-leaderboard-preview">
              <h2 className="font-h uppercase text-lg text-gold mb-3 flex items-center gap-2">
                <Trophy className="w-5 h-5" /> {t('iqc_leaderboard_h')}
              </h2>
              <ChallengeLeaderboardTable rows={leaderboard.top} t={t} />
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ============== Phase: RUNNING ==============
  if (phase === 'running' && scenarioDetail) {
    const total = challenge.scenarios.length;
    const gs = scenarioDetail.game_state;
    return (
      <Layout>
        <div className="max-w-4xl mx-auto" data-testid="iq-challenge-running">
          {/* Progress bar */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-gold">
                {t('iqc_scenario')} {currentIdx + 1} / {total}
              </div>
              <div className="text-xs font-mono text-gold/80">
                {t('iqc_total_so_far')}: <span className="text-gold font-h text-base">{totalScore}</span>
              </div>
            </div>
            <div className="h-2 bg-card-steel rounded-full overflow-hidden">
              <div
                className="h-full bg-gold transition-all"
                style={{ width: `${((currentIdx) / total) * 100}%` }}
                data-testid="iqc-progress-bar"
              />
            </div>
          </div>

          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">
            {scenarioDetail.category === 'offensive' ? t('iq_cat_offensive')
              : scenarioDetail.category === 'defensive' ? t('iq_cat_defensive')
              : t('iq_cat_complete')}
            <span className="text-gold/50"> · {scenarioDetail.difficulty}</span>
          </div>
          <h1 className="font-h uppercase text-3xl mb-3">{scenarioDetail.title}</h1>

          {gs && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5 rounded-lg border-2 border-gold/40 bg-navy-deep p-3">
              <ScoreCell label={t('iq_gs_quarter')} value={gs.quarter} />
              <ScoreCell label={t('iq_gs_clock')} value={gs.clock} accent />
              <ScoreCell label={t('iq_gs_score')} value={gs.score} />
              <ScoreCell label={t('iq_gs_fouls')} value={gs.team_fouls} />
              <ScoreCell
                label={t('iq_gs_possession')}
                value={gs.possession === 'ours' ? t('iq_gs_ours') : t('iq_gs_theirs')}
              />
            </div>
          )}

          <p className="text-gray-300 mb-5 leading-relaxed">{scenarioDetail.situation}</p>

          <div className="rounded-lg overflow-hidden border-2 border-gold/40 bg-navy-deep mb-5">
            <PlayAnimation
              play={{
                id: 'iqc-preview',
                name: scenarioDetail.title,
                court_mode: scenarioDetail.court_mode || 'half',
                steps: buildIQScenarioSteps(scenarioDetail.court_setup, scenarioDetail.situation),
              }}
              rosterMap={{}}
              courtMode={scenarioDetail.court_mode || 'half'}
              autoplay={true}
            />
          </div>

          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-h uppercase tracking-wider text-gold">{t('iq_what_do_you_do')}</div>
            <div className="flex items-center gap-1.5 text-xs text-gold/70 font-mono">
              <Clock className="w-3.5 h-3.5" />
              {t('iq_reaction_tracking')}
            </div>
          </div>
          <div className="space-y-3">
            {(scenarioDetail.options || []).map((o, idx) => (
              <button
                key={o.id}
                onClick={() => decide(o.id)}
                disabled={submitting}
                data-testid={`iqc-option-${o.id}`}
                className="w-full text-left px-5 py-4 rounded-lg border-2 border-gold/30 hover:border-gold hover:bg-gold/5 transition disabled:opacity-50"
              >
                <div className="flex items-start gap-3">
                  <div className="font-h text-gold text-lg w-7 flex-shrink-0">{String.fromCharCode(65 + idx)}</div>
                  <div className="text-white text-base">{o.text}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  // ============== Phase: DONE ==============
  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="iq-challenge-done">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{t('iqc_complete_eyebrow')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl mb-3">{t('iqc_complete_h1')}</h1>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <SummaryCard icon={Trophy} label={t('iqc_summary_total')} value={totalScore} accent />
          <SummaryCard icon={Target} label={t('iqc_summary_correct')} value={`${correctCount}/${challenge.scenarios.length}`} />
          <SummaryCard icon={Brain} label={t('iqc_summary_accuracy')} value={`${Math.round((correctCount / challenge.scenarios.length) * 100)}%`} />
        </div>

        <div className="bg-card-steel border border-gold/30 rounded-lg overflow-hidden mb-6">
          {results.map((r, i) => {
            const Icon = r.result.is_optimal ? CheckCircle2 : XCircle;
            return (
              <div key={r.scenario.id} className="flex items-center gap-3 px-4 py-3 border-b border-gold/10 last:border-b-0">
                <div className="font-h text-gold w-8">#{i + 1}</div>
                <Icon className={`w-5 h-5 ${r.result.is_optimal ? 'text-green-400' : 'text-red-300'}`} />
                <div className="flex-1 min-w-0 font-h uppercase text-sm tracking-wider text-white truncate">{r.scenario.title}</div>
                <div className="font-h text-gold">+{r.result.score_awarded}</div>
              </div>
            );
          })}
        </div>

        {leaderboard && leaderboard.top && leaderboard.top.length > 0 && (
          <div className="mb-6">
            <h2 className="font-h uppercase text-lg text-gold mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5" /> {t('iqc_leaderboard_h')}
            </h2>
            <ChallengeLeaderboardTable rows={leaderboard.top} t={t} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => { setPhase('preview'); setResults([]); setAttemptId(null); setCurrentIdx(0); }}
            data-testid="iqc-retry"
            className="px-5 py-2.5 rounded-md font-h uppercase tracking-wider bg-gold text-navy hover:bg-gold-light"
          >
            {t('iqc_take_again')}
          </button>
          <ShareInviteButton
            url={shareUrl}
            title={challenge.title}
            text={`${t('iqc_share_my_result')}: ${totalScore} IQ · ${shareUrl}`}
            variant="default"
            data-testid="iqc-share-result"
          />
          <Link
            to="/iq-simulator"
            className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold text-sm ms-2"
          >
            <ArrowLeft className="w-4 h-4" /> {t('iq_back_to_hub')}
          </Link>
        </div>
      </div>
    </Layout>
  );
}

function ScoreCell({ label, value, accent = false }) {
  return (
    <div className="text-center">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">{label}</div>
      <div className={`font-h ${accent ? 'text-2xl text-gold' : 'text-lg text-white'}`}>{value}</div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, accent = false }) {
  return (
    <div className={`rounded-lg border-2 p-4 ${accent ? 'border-gold bg-gold/5' : 'border-gold/25 bg-card-steel'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${accent ? 'text-gold' : 'text-gold/70'}`} />
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">{label}</div>
      </div>
      <div className={`font-h ${accent ? 'text-3xl text-gold' : 'text-2xl text-white'}`}>{value}</div>
    </div>
  );
}

function ChallengeLeaderboardTable({ rows, t }) {
  return (
    <div className="bg-card-steel border-2 border-gold/30 rounded-lg overflow-hidden" data-testid="iqc-lb-table">
      <div className="grid grid-cols-12 text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 px-4 py-2 border-b border-gold/20">
        <div className="col-span-1">#</div>
        <div className="col-span-6">{t('iq_lb_player')}</div>
        <div className="col-span-3 text-right">{t('iq_lb_score')}</div>
        <div className="col-span-2 text-right">{t('iq_lb_acc')}</div>
      </div>
      {rows.map(row => {
        const RankIcon = row.rank === 1 ? Crown : row.rank <= 3 ? Medal : null;
        return (
          <div
            key={row.user_id}
            data-testid={`iqc-lb-row-${row.user_id}`}
            className={`grid grid-cols-12 items-center px-4 py-2.5 border-b border-gold/10 last:border-b-0 text-sm ${
              row.is_me ? 'bg-gold/10' : ''
            }`}
          >
            <div className={`col-span-1 font-h ${row.rank <= 3 ? 'text-gold' : 'text-gray-400'} flex items-center gap-1`}>
              {RankIcon && <RankIcon className="w-3.5 h-3.5" />}{row.rank}
            </div>
            <div className="col-span-6 truncate">
              <span className={row.is_me ? 'text-gold font-h uppercase' : 'text-white'}>{row.name}</span>
              {row.is_me && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-gold/70">{t('iq_lb_you')}</span>}
            </div>
            <div className="col-span-3 text-right font-h text-white">{row.total_score}</div>
            <div className="col-span-2 text-right text-gray-400 font-mono">
              {Math.round((row.correct / Math.max(row.attempts, 1)) * 100)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}
